import React, { useState } from 'react';
import { Drawer, Typography, Space, Button, Alert, message } from 'antd';
import { RightOutlined, LoadingOutlined, LinkOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  WalletFamily,
  walletBrowserOptions,
  openWalletBrowser,
  currentPageUrl,
  withLinkHandoff,
} from '../helpers/walletDeepLinks';
import { requestLinkToken } from '../helpers/walletLink';
import { startPhantomConnect } from '../helpers/phantomDeepLink';
import { useMultiWallet } from '../hooks/useMultiWallet';
import { defaultNetworkOfFamily, familyOfNetwork } from '../helpers/walletLink';

const { Text } = Typography;

interface WalletBrowserDrawerProps {
  open: boolean;
  family: WalletFamily | null;
  onClose: () => void;
}

/**
 * Выбор кошелька, в чьём встроенном браузере открыть страницу.
 *
 * Для TRON это единственный способ подключиться с телефона: адаптера TRON у
 * Reown нет. Для Solana — фактически тоже: у Phantom нет WalletConnect. Для EVM
 * — путь в обход WalletConnect: внутри кошелька провайдер уже в странице,
 * подключение мгновенное и не зависит от relay.
 *
 * Переход не мгновенный: перед уходом в кошелёк запрашивается одноразовый токен
 * привязки, без которого страница внутри кошелька не сможет доказать серверу,
 * чей это аккаунт. Отсюда состояние ожидания на кнопке.
 */
export const WalletBrowserDrawer: React.FC<WalletBrowserDrawerProps> = ({ open, family, onClose }) => {
  const { t } = useTranslation();
  const wallet = useMultiWallet();
  const [pendingId, setPendingId] = useState<string | null>(null);
  const options = family ? walletBrowserOptions(family) : [];

  // Сеть, ради которой уходим: активная, если она того же семейства, иначе
  // основная для семейства. Она уезжает в ссылку и определяет, что именно
  // привяжется на той стороне.
  const chain =
    family && familyOfNetwork(wallet.activeNetwork) === family
      ? wallet.activeNetwork
      : family
      ? defaultNetworkOfFamily(family)
      : null;

  /**
   * Ссылка для кошелька, которого нет в списке. Открыть сайт внутри такого
   * кошелька вручную мало: странице нужен токен привязки, иначе она подключит
   * кошелёк и не сможет доказать серверу, чей это аккаунт, — ровно та тихая
   * неудача, из-за которой подключение не доходило до приложения.
   */
  const copyLink = async () => {
    if (!family) return;
    const token = await requestLinkToken(chain, family);
    const url = withLinkHandoff(currentPageUrl(), token, chain, family);
    try {
      await navigator.clipboard.writeText(url);
      message.success(t('Link copied') as string);
    } catch {
      // Буфер обмена бывает недоступен (нет разрешения, http). Показываем
      // ссылку — её можно выделить и скопировать руками.
      message.info(url);
    }
  };

  const viaWalletConnect = async () => {
    if (!chain) return;
    onClose();
    try {
      await wallet.connectViaWalletConnect(chain);
    } catch (e: any) {
      message.error(t('Wallet connector is unavailable. Reload the page or try another browser.') as string);
    }
  };

  const handleOpen = async (optionId: string) => {
    const option = options.find((o) => o.id === optionId);
    if (!option || !family || pendingId) return;
    setPendingId(optionId);
    try {
      // Родной протокол кошелька: токен привязки не нужен вовсе. Ответ
      // приходит сюда же, в приложение, где человек авторизован, — привязка
      // делается обычным запросом под JWT. Токен существует ровно для того,
      // чтобы пережить уход в чужой WebView; здесь ухода нет.
      if (option.mode === 'deeplink') {
        startPhantomConnect();
        return;
      }
      await openWalletBrowser(option, family, chain);
    } finally {
      // Если переход состоялся, размонтирование нас опередит; если кошелёк не
      // установлен, человек останется здесь — и кнопка должна снова нажиматься.
      setPendingId(null);
    }
  };

  return (
    <Drawer
      title={t('Open in a wallet app')}
      placement="bottom"
      onClose={onClose}
      open={open && Boolean(family)}
      height="auto"
      bodyStyle={{
        padding: '16px 16px 32px 16px',
        borderTopLeftRadius: '20px',
        borderTopRightRadius: '20px',
      }}
      headerStyle={{ borderBottom: '1px solid #f0f0f0' }}
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.5 }}>
          {t('The page will open inside the wallet, where the connection happens instantly — without WalletConnect and without switching between apps.')}
        </Text>

        {options.map((option) => (
          <Button
            key={option.id}
            block
            size="large"
            data-testid={`wallet-browser-${option.id}`}
            disabled={pendingId !== null && pendingId !== option.id}
            onClick={() => handleOpen(option.id)}
            style={{
              height: '54px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              textAlign: 'left',
            }}
          >
            <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
              <Text strong style={{ fontSize: 14 }}>
                {option.name}
              </Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {option.hint}
              </Text>
            </span>
            {pendingId === option.id ? (
              <LoadingOutlined style={{ color: '#94a3b8', fontSize: 12 }} />
            ) : (
              <RightOutlined style={{ color: '#94a3b8', fontSize: 12 }} />
            )}
          </Button>
        ))}

        <Button
          block
          size="large"
          icon={<LinkOutlined />}
          onClick={copyLink}
          style={{ height: 46, borderRadius: 12 }}
        >
          {t('Copy a link for any other wallet')}
        </Button>

        {family !== 'tron' && (
          <Button type="text" block onClick={viaWalletConnect} style={{ fontSize: 12 }}>
            {t('Connect over WalletConnect instead')}
          </Button>
        )}

        <Alert
          type="info"
          showIcon
          style={{ borderRadius: 12, fontSize: 12 }}
          message={t(
            'Your wallet is not on the list? Copy the link and open it in that wallet’s built-in browser — the connection will work the same way. The link is personal and lives 15 minutes.'
          )}
        />
      </Space>
    </Drawer>
  );
};

export default WalletBrowserDrawer;
